/**
 * CookieBanner - GDPR/TTDSG consent banner
 * ========================================
 * Consent is stored in the shared `cookie_consent_v1` cookie (scoped to
 * `.kontomatch.com` — see src/lib/cookieConsent.ts) so the choice is shared
 * between the marketing site and this app. The banner only appears when no
 * choice has been made yet on either surface; otherwise we silently respect
 * the stored choice.
 *
 * Analytics is opt-in: PostHog stays opted out until the user accepts.
 */

import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useLang } from '../../shared/i18n';
import { getCookieConsent, setCookieConsent } from '../../lib/cookieConsent';
import posthog from '../../lib/posthog';
import './CookieBanner.scss';

const OPEN_EVENT = 'open-cookie-banner';

/** Footer etc. can call this to let the user change their mind. */
export function openCookieBanner() {
    window.dispatchEvent(new Event(OPEN_EVENT));
}

function applyConsent(analytics: boolean) {
    // Persist to the shared root-domain cookie (read by both marketing and app).
    setCookieConsent(analytics);

    // Toggle PostHog capturing live, without needing a reload.
    try {
        if (analytics) {
            posthog.opt_in_capturing();
        } else {
            posthog.opt_out_capturing();
        }
    } catch { /* posthog may not be initialized (missing key) */ }
}

const CookieBanner: React.FC = () => {
    const { t } = useLang();
    const [visible, setVisible] = useState(false);

    useEffect(() => {
        // Shared cookie already set (here or on the marketing site) → respect it.
        if (!getCookieConsent()) setVisible(true);
        const onOpen = () => setVisible(true);
        window.addEventListener(OPEN_EVENT, onOpen);
        return () => window.removeEventListener(OPEN_EVENT, onOpen);
    }, []);

    if (!visible) return null;

    const handleAccept = () => {
        applyConsent(true);
        setVisible(false);
    };

    const handleReject = () => {
        applyConsent(false);
        setVisible(false);
    };

    return (
        <div className="cookie-banner" role="dialog" aria-live="polite" aria-label={t('cookieBannerTitle')}>
            <div className="cookie-banner__inner">
                <div className="cookie-banner__content">
                    <h3 className="cookie-banner__title">{t('cookieBannerTitle')}</h3>
                    <p className="cookie-banner__text">
                        {t('cookieBannerText')}{' '}
                        <Link to="/legal/privacy" className="cookie-banner__link">
                            {t('cookieBannerLearnMore')}
                        </Link>
                    </p>
                </div>
                <div className="cookie-banner__actions">
                    <button
                        type="button"
                        className="cookie-banner__btn cookie-banner__btn--secondary"
                        onClick={handleReject}
                    >
                        {t('cookieBannerEssentialOnly')}
                    </button>
                    <button
                        type="button"
                        className="cookie-banner__btn cookie-banner__btn--primary"
                        onClick={handleAccept}
                    >
                        {t('cookieBannerAcceptAll')}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default CookieBanner;
